import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Clock } from "lucide-react";
import { format, fromUnixTime } from "date-fns";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function UnixTimestampConverter() {
  const [timestamp, setTimestamp] = useState<string>(Math.floor(Date.now() / 1000).toString());
  const [dateInput, setDateInput] = useState<string>("");
  const [humanReadable, setHumanReadable] = useState<string>("");
  const [copiedTs, setCopiedTs] = useState(false);
  const [copiedDate, setCopiedDate] = useState(false);

  useEffect(() => {
    // Set initial dateInput to match current timestamp
    setDateInput(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  }, []);

  const handleTimestampChange = (val: string) => {
    setTimestamp(val);
    const tsNumber = Number(val);
    if (!isNaN(tsNumber) && val.trim() !== "") {
      try {
        // Assume seconds if < 10000000000 (roughly year 2286), else ms
        const isSeconds = tsNumber < 10000000000;
        const dateObj = isSeconds ? fromUnixTime(tsNumber) : new Date(tsNumber);
        if (!isNaN(dateObj.getTime())) {
          setHumanReadable(format(dateObj, "yyyy-MM-dd HH:mm:ss OOOO"));
          setDateInput(format(dateObj, "yyyy-MM-dd'T'HH:mm"));
        } else {
          setHumanReadable("Invalid Date");
        }
      } catch (e) {
        setHumanReadable("Invalid Date");
      }
    } else {
      setHumanReadable("");
    }
  };

  const handleDateChange = (val: string) => {
    setDateInput(val);
    if (val) {
      const dateObj = new Date(val);
      if (!isNaN(dateObj.getTime())) {
        const newTs = Math.floor(dateObj.getTime() / 1000).toString();
        setTimestamp(newTs);
        setHumanReadable(format(dateObj, "yyyy-MM-dd HH:mm:ss OOOO"));
      } else {
        setHumanReadable("Invalid Date");
      }
    }
  };

  // Initial trigger
  useEffect(() => {
    if (timestamp && !humanReadable) {
      handleTimestampChange(timestamp);
    }
  }, [timestamp, humanReadable]);

  const setToNow = () => {
    const now = Math.floor(Date.now() / 1000).toString();
    handleTimestampChange(now);
  };

  return (
    <ToolPageLayout
      title="Unix Timestamp Converter Online"
      description="Convert between Unix timestamps and human-readable dates instantly with our free online tool."
      h1="Unix Timestamp Converter"
      category="DateTime Tools"
      categoryPath="/datetime-tools"
      howToUse={[
        "Enter a Unix timestamp to convert to a readable date.",
        "Or, select a date to convert it to a Unix timestamp.",
        "Copy your desired format."
      ]}
      faq={[
        { question: "What is a Unix timestamp?", answer: "A Unix timestamp is the number of seconds that have elapsed since January 1, 1970 (UTC)." }
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-10">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4 bg-slate-50 border border-slate-200 p-6 rounded-xl relative">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={setToNow}
              className="absolute top-4 right-4 text-xs h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Clock className="w-3 h-3 mr-1" />
              Set to Now
            </Button>
            <div className="space-y-2">
              <Label className="text-base font-semibold">Unix Timestamp</Label>
              <div className="text-xs text-slate-500 mb-2">Seconds or milliseconds since Jan 01 1970. (UTC)</div>
              <div className="flex gap-2">
                <Input 
                  value={timestamp}
                  onChange={(e) => handleTimestampChange(e.target.value)}
                  className="font-mono text-lg"
                />
                <Button
                  variant="outline"
                  className="shrink-0"
                  aria-label="Copy timestamp to clipboard"
                  onClick={() => {
                    copyToClipboard(timestamp);
                    setCopiedTs(true);
                    setTimeout(() => setCopiedTs(false), 2000);
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {copiedTs && <span className="text-xs text-green-600">Copied!</span>}
            </div>
            
            <div className="pt-4 border-t border-slate-200 space-y-1">
              <Label className="text-sm text-slate-500 uppercase tracking-wider">Human Readable Output</Label>
              <div className="text-lg font-medium text-slate-900 bg-white border border-slate-200 rounded-md p-3">
                {humanReadable || "-"}
              </div>
            </div>
          </div>

          <div className="space-y-4 bg-slate-50 border border-slate-200 p-6 rounded-xl">
            <div className="space-y-2">
              <Label className="text-base font-semibold">Date & Time to Timestamp</Label>
              <div className="text-xs text-slate-500 mb-2">Select a date to convert it to a Unix timestamp.</div>
              <div className="flex gap-2">
                <Input 
                  type="datetime-local"
                  value={dateInput}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="text-lg"
                />
                <Button
                  variant="outline"
                  className="shrink-0"
                  aria-label="Copy date representation to clipboard"
                  onClick={() => {
                    copyToClipboard(dateInput);
                    setCopiedDate(true);
                    setTimeout(() => setCopiedDate(false), 2000);
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {copiedDate && <span className="text-xs text-green-600">Copied!</span>}
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
